/**
 * Pure time maths for the Schedule page.
 *
 * Everything here is deliberately free of Solid, fetch and Date.now() so the countdown can be
 * tested at fixed instants: the callers pass `nowMs` in.
 *
 * TIMES ARE LOCAL WALL CLOCK. A schedule item is typed as "13:05" by someone standing at the
 * event, and is resolved against local midnight of the day it is being run on
 * (`scheduleDayStartMs`). That day is the LOCAL day rather than the UTC day the alarms parquet is
 * partitioned by — east of Greenwich the two disagree for the first hours of the evening before,
 * which is exactly when a schedule gets entered.
 *
 * A SCHEDULE IS NOT TIED TO A DATE. It is a daily plan — a running order that is reused from one
 * day to the next — so a project holds a LIBRARY of named plans and one of them is the one being
 * run. The day only ever enters at the moment a plan is resolved against the clock, which is
 * always today. Nothing here stores, or needs, a date.
 */

/** One row of the schedule. `time` is "HH:MM" as stored; `id` is only for keyed rendering. */
export interface ScheduleItem {
    id: string;
    /** Local wall-clock time of day, canonically "HH:MM". */
    time: string;
    name: string;
}

/** One named daily plan: a running order that can be reused on any day. */
export interface SchedulePlan {
    /**
     * Stable across saves, unlike `ScheduleItem.id`.
     *
     * It has to be: `activePlanId` points at it, and a plan re-identified on every read would lose
     * track of which plan the project is running the moment anyone reloaded.
     */
    id: string;
    name: string;
    items: ScheduleItem[];
}

/** The shape stored in gp50.project_objects.json under object_name = 'schedule'. */
export interface ScheduleObject {
    version: 2;
    /** Every plan the project has, in the order they were created. */
    plans: SchedulePlan[];
    /** The plan the countdown runs. null only when there are no plans at all. */
    activePlanId: string | null;
    /** ISO instant of the last Save, for the "saved at" line. Absent on hand-written rows. */
    updated_at?: string;
}

/**
 * A fresh, collision-proof row id.
 *
 * WHY NOT A COUNTER. The first version minted `draft-1`, `draft-2`, ... from a module-level
 * counter that restarts at 0 on every page load, and those ids were written into the stored
 * object. So on the next visit a schedule already holding `draft-1..draft-3` would hand the very
 * first "Add event" the id `draft-1` again - and the editor writes a field with
 * `setDraft('items', row => row.id === id, ...)`, which updates EVERY matching row. Two rows
 * shared an id, so typing in one typed into the other and deleting one deleted both.
 *
 * randomUUID where it exists, and a time-plus-random fallback for any context without it (an
 * insecure origin, or an older test environment), which is still unique in practice because the
 * counter component alone cannot repeat within a session.
 */
let idFallbackCounter = 0;
export function newScheduleItemId(): string {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (typeof c?.randomUUID === 'function') {
        return c.randomUUID();
    }
    idFallbackCounter += 1;
    return `sched-${Date.now().toString(36)}-${idFallbackCounter.toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
}

export const SCHEDULE_OBJECT_NAME = 'schedule';
export const SCHEDULE_OBJECT_VERSION = 2 as const;

/** What the first plan is called, including the one a version-1 schedule is migrated into. */
export const DEFAULT_PLAN_NAME = 'Daily plan';

/**
 * A fresh plan id.
 *
 * Separate from `newScheduleItemId` only so the two cannot be confused at a call site: item ids
 * are re-minted on every read and plan ids must survive, and the prefix makes a stored object
 * readable when someone is looking at the jsonb by hand.
 */
export function newSchedulePlanId(): string {
    return `plan-${newScheduleItemId()}`;
}

/** An empty plan under a given name. */
export function newSchedulePlan(name: string, items: readonly ScheduleItem[] = []): SchedulePlan {
    return {
        id: newSchedulePlanId(),
        name: String(name ?? '').trim() || DEFAULT_PLAN_NAME,
        items: items.map((i) => ({ ...i })),
    };
}

/**
 * `base`, or "base (2)", "base (3)"... — whichever is not already in `existing`.
 *
 * Duplicate names are not forbidden anywhere (the id is what identifies a plan), but two rows
 * reading "Race day" in the same dropdown is a trap, so Copy and New steer around it.
 */
export function uniquePlanName(base: string, existing: readonly string[]): string {
    const wanted = String(base ?? '').trim() || DEFAULT_PLAN_NAME;
    const taken = new Set(existing.map((n) => String(n ?? '').trim().toLowerCase()));
    if (!taken.has(wanted.toLowerCase())) {return wanted;}
    for (let n = 2; n < 1000; n += 1) {
        const candidate = `${wanted} (${n})`;
        if (!taken.has(candidate.toLowerCase())) {return candidate;}
    }
    // 998 plans of the same name is not a case worth more code than this.
    return `${wanted} (${Date.now().toString(36)})`;
}

/**
 * Minutes since local midnight for a typed time, or null if it is not a time.
 *
 * Accepts "13:05", "9:5", "1305", "905" and "13:05:30" (seconds are dropped — the schedule has
 * minute resolution and storing seconds would imply a precision the typist did not give).
 * Rejects 24:00 and above: a schedule entry at 24:00 belongs to the next day's object.
 */
export function parseScheduleTimeToMinutes(input: unknown): number | null {
    if (input === null || input === undefined) {return null;}
    const raw = String(input).trim();
    if (!raw) {return null;}

    let hours: number;
    let minutes: number;

    const colon = /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/.exec(raw);
    if (colon) {
        hours = Number(colon[1]);
        minutes = Number(colon[2]);
    } else {
        // Bare digits: "1305" -> 13:05, "905" -> 9:05. Fewer than 3 digits is an hour, not a time,
        // and is rejected rather than guessed — "13" could equally mean 13:00 or 00:13.
        const bare = /^(\d{3,4})$/.exec(raw);
        if (!bare) {return null;}
        const digits = bare[1];
        hours = Number(digits.slice(0, digits.length - 2));
        minutes = Number(digits.slice(-2));
    }

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {return null;}
    if (hours < 0 || hours > 23) {return null;}
    if (minutes < 0 || minutes > 59) {return null;}
    return hours * 60 + minutes;
}

/**
 * A row the user has half-filled: one of time/name present, the other missing.
 *
 * A WHOLLY EMPTY row is deliberately not incomplete — it is the blank "new row" the editor hands
 * you, and buildScheduleObjectJson drops it silently. Only a row that has been started and left
 * unfinished is worth warning about, because that is the one whose loss would surprise someone.
 */
export function isRowIncomplete(row: { time?: unknown; name?: unknown } | null | undefined): boolean {
    const hasTime = normalizeScheduleTime(row?.time) !== null;
    const hasName = String(row?.name ?? '').trim() !== '';
    if (!hasTime && !hasName) {return false;}
    return !hasTime || !hasName;
}

/** Canonical "HH:MM" for storage and display, or null if the input is not a time. */
export function normalizeScheduleTime(input: unknown): string | null {
    const mins = parseScheduleTimeToMinutes(input);
    if (mins === null) {return null;}
    return formatMinutesOfDay(mins);
}

/** "HH:MM" from minutes since midnight. */
export function formatMinutesOfDay(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Local midnight of a "YYYYMMDD" or "YYYY-MM-DD" day, in epoch ms.
 *
 * Built with the Date(y, m, d) constructor rather than Date.parse: `new Date('2026-09-19')` is
 * parsed as UTC midnight by spec, which would shift every schedule item by the local offset.
 */
export function scheduleDayStartMs(dayYmd: string): number | null {
    const digits = String(dayYmd ?? '').replace(/[-/]/g, '');
    if (!/^\d{8}$/.test(digits)) {return null;}
    const year = Number(digits.slice(0, 4));
    const month = Number(digits.slice(4, 6));
    const day = Number(digits.slice(6, 8));
    if (month < 1 || month > 12 || day < 1 || day > 31) {return null;}
    const d = new Date(year, month - 1, day, 0, 0, 0, 0);
    // Round-trip check rejects 20260231 rather than silently rolling it into March.
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {return null;}
    return d.getTime();
}

/** Local "YYYYMMDD" for an instant. The LOCAL day — see the file header. */
export function localDayYmd(ms: number = Date.now()): string {
    const d = new Date(ms);
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

/** "YYYY-MM-DD" from a "YYYYMMDD" or already-hyphenated day, for the /projects/object API. */
export function toDashedDay(dayYmd: string): string {
    const digits = String(dayYmd ?? '').replace(/[-/]/g, '');
    if (!/^\d{8}$/.test(digits)) {return String(dayYmd ?? '');}
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
}

/** Outcome of a whole-schedule time shift. */
export interface ScheduleShiftResult {
    ok: boolean;
    /** The shifted rows on success; the untouched input on failure. */
    items: ScheduleItem[];
    /** Present when ok === false; already human-readable. */
    reason?: string;
}

/**
 * Move every timed row by `deltaMinutes` (negative moves earlier).
 *
 * ALL OR NOTHING. If any row would land outside the day the whole shift is refused and the caller
 * gets its input back unchanged. The alternative - clamping the offenders to 00:00 or 23:59 -
 * would silently collapse events on top of each other and quietly destroy a schedule that took
 * real effort to type, which is far worse than making the user pick a smaller offset.
 *
 * Rows with no usable time (the blank row the editor always offers, or a half-typed one) are
 * passed through untouched rather than blocking the shift: they have no time to move.
 */
export function shiftScheduleItems(
    items: readonly ScheduleItem[] | null | undefined,
    deltaMinutes: number,
): ScheduleShiftResult {
    const source = (items ?? []).map((i) => ({ ...i }));
    if (!Number.isFinite(deltaMinutes) || Math.trunc(deltaMinutes) === 0) {
        return { ok: true, items: source };
    }
    const delta = Math.trunc(deltaMinutes);

    const shifted: ScheduleItem[] = [];
    for (const item of source) {
        const mins = parseScheduleTimeToMinutes(item.time);
        if (mins === null) {
            shifted.push(item);
            continue;
        }
        const moved = mins + delta;
        if (moved < 0 || moved > 23 * 60 + 59) {
            return {
                ok: false,
                items: source,
                reason:
                    delta < 0
                        ? `Shifting by ${delta} min would move "${item.name || item.time}" before midnight.`
                        : `Shifting by +${delta} min would move "${item.name || item.time}" past midnight.`,
            };
        }
        shifted.push({ ...item, time: formatMinutesOfDay(moved) });
    }
    return { ok: true, items: shifted };
}

/**
 * Change one event's time and carry everything after it by the same amount.
 *
 * The race-postponed case: the 14:45 briefing slips to 15:00, and the whole rest of the day slips
 * 15 minutes with it, gaps intact. Only events scheduled STRICTLY AFTER the edited one at its
 * ORIGINAL time move - anything earlier has already happened or is unaffected, and an event at the
 * exact same minute is left alone because "after" cannot be decided for it from the times alone.
 *
 * The cascade is skipped, rather than the edit refused, when:
 *  - the row had no usable time before (a new row being filled in is not a postponement),
 *  - the time did not actually change,
 *  - or the shift would push a later event outside the day. In that last case `ok` is false and
 *    `reason` explains, but the returned list still carries the direct edit: the user asked for
 *    that one change and it should not be silently thrown away because the knock-on failed.
 */
export function retimeScheduleItem(
    items: readonly ScheduleItem[] | null | undefined,
    id: string,
    oldTime: unknown,
    newTime: unknown,
): ScheduleShiftResult {
    const source = (items ?? []).map((i) => ({ ...i }));

    // No such row: nothing was edited, so nothing may be carried. Without this the followers are
    // still computed from `oldTime` and the rest of the day shifts on behalf of an event that is
    // not there.
    if (!source.some((i) => i.id === id)) {
        return { ok: true, items: source };
    }

    const normalizedNew = normalizeScheduleTime(newTime);

    const applyDirectEdit = (list: ScheduleItem[]): ScheduleItem[] =>
        list.map((i) => (i.id === id && normalizedNew !== null ? { ...i, time: normalizedNew } : i));

    const oldMinutes = parseScheduleTimeToMinutes(oldTime);
    const newMinutes = parseScheduleTimeToMinutes(newTime);
    if (oldMinutes === null || newMinutes === null) {
        return { ok: true, items: applyDirectEdit(source) };
    }

    const delta = newMinutes - oldMinutes;
    if (delta === 0) {
        return { ok: true, items: applyDirectEdit(source) };
    }

    // "After" is judged against the ORIGINAL time, not the new one. Judging against the new time
    // would change which events move depending on the direction of the edit.
    const followers = source.filter((i) => {
        if (i.id === id) {return false;}
        const mins = parseScheduleTimeToMinutes(i.time);
        return mins !== null && mins > oldMinutes;
    });

    for (const follower of followers) {
        const mins = parseScheduleTimeToMinutes(follower.time) as number;
        const moved = mins + delta;
        if (moved < 0 || moved > 23 * 60 + 59) {
            return {
                ok: false,
                items: applyDirectEdit(source),
                reason:
                    `Moved this event, but not the ${followers.length} after it: ` +
                    `${delta > 0 ? '+' : ''}${delta} min would push "${follower.name || follower.time}" outside the day.`,
            };
        }
    }

    const followerIds = new Set(followers.map((i) => i.id));
    const next = source.map((item) => {
        if (item.id === id) {
            return { ...item, time: formatMinutesOfDay(newMinutes) };
        }
        if (!followerIds.has(item.id)) {return item;}
        const mins = parseScheduleTimeToMinutes(item.time) as number;
        return { ...item, time: formatMinutesOfDay(mins + delta) };
    });
    return { ok: true, items: next };
}

/** A schedule item resolved to an absolute instant on its day. */
export interface ResolvedScheduleItem extends ScheduleItem {
    /** Epoch ms of the item, local wall clock on the schedule's day. */
    atMs: number;
    minutesOfDay: number;
}

/**
 * Sort by time and drop unparseable rows.
 *
 * Ties keep their entered order (Array.prototype.sort is stable), so two events at 13:05 read in
 * the order the user typed them rather than an arbitrary one.
 */
export function resolveScheduleItems(
    items: readonly ScheduleItem[] | null | undefined,
    dayYmd: string,
): ResolvedScheduleItem[] {
    const dayStart = scheduleDayStartMs(dayYmd);
    if (dayStart === null) {return [];}
    const resolved: ResolvedScheduleItem[] = [];
    for (const item of items ?? []) {
        const minutesOfDay = parseScheduleTimeToMinutes(item?.time);
        if (minutesOfDay === null) {continue;}
        resolved.push({
            ...item,
            minutesOfDay,
            atMs: withMinutesOfDay(dayStart, minutesOfDay),
        });
    }
    resolved.sort((a, b) => a.minutesOfDay - b.minutesOfDay);
    return resolved;
}

/**
 * Local midnight plus a wall-clock time of day.
 *
 * setHours, not `dayStart + minutes * 60_000`: on a DST transition day local midnight plus 13 h of
 * milliseconds is 12:00 or 14:00, not 13:00. setHours re-resolves the offset for the target
 * wall-clock time, which is what "the briefing is at 13:00" means.
 */
function withMinutesOfDay(dayStartMs: number, minutesOfDay: number): number {
    const d = new Date(dayStartMs);
    d.setHours(Math.floor(minutesOfDay / 60), minutesOfDay % 60, 0, 0);
    return d.getTime();
}

export interface ScheduleStatus {
    /** All items on the day, sorted, whatever their state. */
    items: ResolvedScheduleItem[];
    /** The first item still in the future, or null once the day is done. */
    next: ResolvedScheduleItem | null;
    /** The most recent item already passed, or null before the day starts. */
    current: ResolvedScheduleItem | null;
    /** ms until `next`; null when there is no next. Never negative. */
    msToNext: number | null;
    /** ms since `current`; null when there is no current. */
    msSinceCurrent: number | null;
    /**
     * 0..1 through the gap between `current` and `next`, for the progress bar.
     * null unless both ends exist (before the first and after the last there is no gap to fill).
     */
    gapProgress: number | null;
}

/**
 * Where we are in the day.
 *
 * An item exactly at `nowMs` counts as PASSED, not as next: at the instant the start gun fires the
 * useful readout is time since the start, not a countdown frozen at 0:00.
 */
export function resolveScheduleStatus(
    items: readonly ScheduleItem[] | null | undefined,
    dayYmd: string,
    nowMs: number,
): ScheduleStatus {
    const resolved = resolveScheduleItems(items, dayYmd);

    let current: ResolvedScheduleItem | null = null;
    let next: ResolvedScheduleItem | null = null;
    for (const item of resolved) {
        if (item.atMs <= nowMs) {
            current = item;
        } else {
            next = item;
            break;
        }
    }

    const msToNext = next ? Math.max(0, next.atMs - nowMs) : null;
    const msSinceCurrent = current ? Math.max(0, nowMs - current.atMs) : null;

    let gapProgress: number | null = null;
    if (current && next) {
        const span = next.atMs - current.atMs;
        gapProgress = span > 0 ? Math.min(1, Math.max(0, (nowMs - current.atMs) / span)) : 1;
    }

    return { items: resolved, next, current, msToNext, msSinceCurrent, gapProgress };
}

/**
 * "2:45" under an hour, "1:02:45" at or over one — the shape the page was specified with.
 * Rounds UP to the second so a countdown reads 0:01 for the whole of the last second and hits
 * 0:00 only at the event, instead of showing 0:00 for a second beforehand.
 */
export function formatCountdown(ms: number | null | undefined): string {
    if (ms === null || ms === undefined || !Number.isFinite(ms)) {return '—';}
    const total = Math.ceil(Math.max(0, ms) / 1000);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Same shape as formatCountdown but for elapsed time, which rounds DOWN: 0:00 for the first second
 * after an event is correct, where rounding up would jump straight to 0:01.
 */
export function formatElapsed(ms: number | null | undefined): string {
    if (ms === null || ms === undefined || !Number.isFinite(ms)) {return '—';}
    const total = Math.floor(Math.max(0, ms) / 1000);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Urgency band for the countdown colour. */
export type ScheduleUrgency = 'imminent' | 'soon' | 'normal';

export function resolveUrgency(msToNext: number | null): ScheduleUrgency {
    if (msToNext === null) {return 'normal';}
    if (msToNext <= 60_000) {return 'imminent';}
    if (msToNext <= 300_000) {return 'soon';}
    return 'normal';
}

/**
 * Parse whatever `GET /projects/object` handed back into a schedule library.
 *
 * The endpoint returns the jsonb column, which may arrive already parsed or as a string depending
 * on how it was written, so both are accepted. Anything unrecognisable yields an empty library
 * rather than throwing — a corrupt object must not take the page down.
 *
 * VERSION 1 OBJECTS ARE MIGRATED, not rejected: the first Schedule stored a bare `items` array
 * against a dated row, and that list becomes a single plan named DEFAULT_PLAN_NAME. It costs four
 * lines here and means nobody has to retype a schedule that already exists.
 */
export function parseScheduleObject(value: unknown): ScheduleObject {
    const empty: ScheduleObject = { version: SCHEDULE_OBJECT_VERSION, plans: [], activePlanId: null };
    if (value === null || value === undefined) {return empty;}

    let parsed: unknown = value;
    if (typeof parsed === 'string') {
        const trimmed = parsed.trim();
        if (!trimmed) {return empty;}
        try {
            parsed = JSON.parse(trimmed);
        } catch {
            return empty;
        }
    }
    // Double-encoded: a jsonb string that itself holds JSON, which is what you get if a caller
    // stringifies before POSTing a value that was already a string.
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            return empty;
        }
    }

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {return empty;}
    const obj = parsed as Record<string, unknown>;
    const updatedAt = typeof obj.updated_at === 'string' ? obj.updated_at : undefined;

    const rawPlans = Array.isArray(obj.plans) ? obj.plans : null;
    const plans: SchedulePlan[] = [];
    const usedIds = new Set<string>();

    if (rawPlans) {
        rawPlans.forEach((rawPlan, index) => {
            if (!rawPlan || typeof rawPlan !== 'object' || Array.isArray(rawPlan)) {return;}
            const plan = rawPlan as Record<string, unknown>;
            // A blank or repeated id is replaced rather than dropped: the plan's content is the
            // part someone typed, and two plans sharing an id would make every edit hit both.
            let id = String(plan.id ?? '').trim();
            if (!id || usedIds.has(id)) {id = newSchedulePlanId();}
            usedIds.add(id);
            plans.push({
                id,
                name: String(plan.name ?? '').trim() || `${DEFAULT_PLAN_NAME} ${index + 1}`,
                items: parseScheduleItems(plan.items),
            });
        });
    } else if (Array.isArray(obj.items)) {
        const items = parseScheduleItems(obj.items);
        if (items.length > 0) {
            const plan = newSchedulePlan(DEFAULT_PLAN_NAME, items);
            usedIds.add(plan.id);
            plans.push(plan);
        }
    }

    // A stored active id that no longer names a plan (someone else deleted it) falls back to the
    // first plan rather than to nothing: a library with plans in it always has one running.
    const storedActive = String(obj.activePlanId ?? '').trim();
    const activePlanId = usedIds.has(storedActive) ? storedActive : (plans[0]?.id ?? null);

    return { version: SCHEDULE_OBJECT_VERSION, plans, activePlanId, updated_at: updatedAt };
}

/** The rows of one plan, cleaned. Shared by both of the shapes parseScheduleObject accepts. */
function parseScheduleItems(raw: unknown): ScheduleItem[] {
    const items: ScheduleItem[] = [];
    if (!Array.isArray(raw)) {return items;}
    raw.forEach((entry) => {
        if (!entry || typeof entry !== 'object') {return;}
        const row = entry as Record<string, unknown>;
        const time = normalizeScheduleTime(row.time);
        if (time === null) {return;}
        const name = String(row.name ?? '').trim();
        if (!name) {return;}
        items.push({
            // A FRESH id, never the stored one. The id is a render key and an edit target, not
            // part of the event's identity - two events may legitimately share a time AND a name
            // ("Race 2 warning" twice over, say) and must still be editable apart. Re-minting here
            // also repairs any schedule already saved with the duplicate ids the old counter
            // produced, without a migration.
            id: newScheduleItemId(),
            time,
            name,
        });
    });
    return items;
}

/**
 * The payload for POST /projects/object. Rows without both a valid time and a name are dropped
 * here rather than stored, so every saved plan is directly usable by the countdown.
 *
 * Plans themselves are NOT dropped when they end up empty: an empty plan is a named container
 * somebody deliberately created, and binning it on save would delete their work as they were
 * still typing the name for it.
 */
export function buildScheduleObjectJson(
    plans: readonly SchedulePlan[],
    activePlanId: string | null,
    nowMs: number = Date.now(),
): string {
    const clean: SchedulePlan[] = [];
    for (const plan of plans ?? []) {
        if (!plan) {continue;}
        const items: ScheduleItem[] = [];
        for (const item of plan.items ?? []) {
            const time = normalizeScheduleTime(item?.time);
            const name = String(item?.name ?? '').trim();
            if (time === null || !name) {continue;}
            // The id travels with the row so the stored object stays self-describing, but it is
            // NOT read back - parseScheduleObject mints a new one. Nothing downstream may treat it
            // as a stable identity across a save.
            items.push({ id: item.id, time, name });
        }
        items.sort((a, b) => a.time.localeCompare(b.time));
        clean.push({
            id: plan.id,
            name: String(plan.name ?? '').trim() || DEFAULT_PLAN_NAME,
            items,
        });
    }

    const active = clean.some((p) => p.id === activePlanId) ? activePlanId : (clean[0]?.id ?? null);
    const payload: ScheduleObject = {
        version: SCHEDULE_OBJECT_VERSION,
        plans: clean,
        activePlanId: active,
        updated_at: new Date(nowMs).toISOString(),
    };
    return JSON.stringify(payload);
}

/**
 * Whether two lists of events would store identically — the per-plan half of the dirty check.
 *
 * Compares normalized (time, name) pairs only: `id` is a rendering key, and a row that has been
 * dragged around without changing its time or name is not an edit worth enabling Save for.
 * Blank rows are ignored on both sides, since buildScheduleObjectJson drops them anyway.
 */
export function scheduleItemsEqual(
    a: readonly ScheduleItem[] | null | undefined,
    b: readonly ScheduleItem[] | null | undefined,
): boolean {
    const norm = (list: readonly ScheduleItem[] | null | undefined) =>
        (list ?? [])
            .map((i) => ({
                time: normalizeScheduleTime(i?.time),
                name: String(i?.name ?? '').trim(),
            }))
            .filter((i) => i.time !== null && i.name !== '')
            .map((i) => `${i.time}\t${i.name}`)
            .sort()
            .join('\n');
    return norm(a) === norm(b);
}

/**
 * The same check across a whole library: order, names and rows.
 *
 * Plan ORDER counts (it is the order of the dropdown) and so does the NAME, because renaming a
 * plan is exactly the kind of change a Save button should appear for. Plan ids do not: a plan
 * rebuilt with the same name and the same rows would store identically.
 */
export function schedulePlansEqual(
    a: readonly SchedulePlan[] | null | undefined,
    b: readonly SchedulePlan[] | null | undefined,
): boolean {
    const left = a ?? [];
    const right = b ?? [];
    if (left.length !== right.length) {return false;}
    for (let i = 0; i < left.length; i += 1) {
        if (String(left[i]?.name ?? '').trim() !== String(right[i]?.name ?? '').trim()) {return false;}
        if (!scheduleItemsEqual(left[i]?.items, right[i]?.items)) {return false;}
    }
    return true;
}
